import * as React from 'react'
import { API, OrderType, RemoteResource, ResourceType, ResourcesOrdering } from '../../services/api'
import { isTimelineOrderBy } from '../GallerySort/gallerySortParams'
import usePagination from '../../utils/usePagination'
import { useWindowedResources } from './useWindowedResources.hook'
import { FlatMonth, TimelineIndex } from './timelineIndex'
import { VisibleRange } from '../JustifiedGallery/visibleRange'
import { GalleryScrollTarget } from '../JustifiedGallery/JustifiedGallery.hook'

const PHOTOS_PER_PAGE = 60
const START_PAGE = 0
/** items around the visible range kept loaded in virtual mode */
const VISIBLE_PAD = 25
/** scrubber position updates are throttled to visible changes */
const FRACTION_EPSILON = 0.002

export interface UseGalleryViewOptions {
  workspaceId: string
  perPage?: number
  startPage?: number
  albumId?: string
  publicPhotos?: boolean
  query?: string
  tags?: Array<string>
  personIds?: Array<string>
  ownerIds?: Array<string>
  deviceId?: string
  placeId?: string
  type?: ResourceType
  dateFrom?: Date
  dateTo?: Date
  isFavorite?: boolean
  orderBy?: ResourcesOrdering
  order?: OrderType
  /** photo preview overlay open — suspends window trimming */
  previewOpen: boolean
}

export interface GalleryScrubberState {
  timeline: TimelineIndex
  fraction: number
  onJump: (month: FlatMonth) => void
}

export interface UseGalleryViewResult {
  photos: Array<RemoteResource>
  windowStart: number
  total: number
  isFetching: boolean
  virtualMode: boolean
  sentinelDisabled: boolean
  onSentinelReached: () => void
  onNearStart: () => void
  onVisibleRangeChange: (range: VisibleRange) => void
  scrubber: GalleryScrubberState | null
  galleryScrollTarget: GalleryScrollTarget | null
  onScrollTargetApplied: () => void
  reset: () => void
}

export const useGalleryView = ({
  workspaceId,
  perPage,
  startPage,
  albumId,
  publicPhotos,
  query,
  tags,
  personIds,
  ownerIds,
  deviceId,
  placeId,
  type,
  dateFrom,
  dateTo,
  isFavorite,
  orderBy = 'date_taken',
  order = 'desc',
  previewOpen,
}: UseGalleryViewOptions): UseGalleryViewResult => {
  // The timeline endpoint rejects free-text search and orderings other than
  // date_taken/date_uploaded; its buckets are also always newest-first, so an ascending
  // list would not line up with the prefix sums buildTimelineIndex derives from them.
  const virtualEligible = !query && isTimelineOrderBy(orderBy) && order === 'desc'

  const windowed = useWindowedResources({
    enabled: virtualEligible,
    workspaceId,
    filters: {
      albumId,
      publicPhotos,
      tags,
      personIds,
      ownerIds,
      deviceId,
      placeId,
      type,
      dateFrom,
      dateTo,
      isFavorite,
      orderBy,
      order,
    },
    holdTrim: previewOpen,
  })
  const fallbackMode = windowed.mode === 'fallback'
  const virtualMode = windowed.mode === 'virtual'

  const fetchResourcesPage = async (page: number, count: number) => {
    const result = await API.getInstance().fetchResources({
      workspaceId,
      from: page * count,
      count,
      albumId,
      public: publicPhotos,
      query,
      tags,
      personIds,
      ownerIds,
      deviceId,
      placeId,
      type,
      dateFrom,
      dateTo,
      isFavorite,
      orderBy,
      order,
    })
    // surface the failure so pagination doesn't read an errored fetch as the end of the list
    if (!result.ok) throw new Error('fetchResources failed')
    return result.elements
  }
  const pagination = usePagination<RemoteResource>({
    fetchPage: fetchResourcesPage,
    perPage: perPage ?? PHOTOS_PER_PAGE,
    startPage: startPage ?? START_PAGE,
  })
  const { reset: paginationReset, fetchNext: paginationFetchNext } = pagination

  const [fraction, setFraction] = React.useState(0)

  React.useEffect(() => {
    paginationReset()
    // the window restarts at index 0; a kept fraction would leave the scrubber
    // pointing at the old position until the next scroll reports one
    setFraction(0)
  }, [
    workspaceId,
    albumId,
    query,
    publicPhotos,
    tags,
    personIds,
    ownerIds,
    deviceId,
    placeId,
    type,
    dateFrom,
    dateTo,
    isFavorite,
    orderBy,
    order,
  ])

  // sequential first-page fetch only drives fallback mode; virtual mode self-initializes.
  // swallow rejection — a failed page keeps allFetched false, so the sentinel retries on the next scroll
  React.useEffect(() => {
    if (fallbackMode && pagination.data.length === 0) {
      setTimeout(() => paginationFetchNext().catch(() => {}))
    }
  }, [fallbackMode, pagination.data.length])

  const onVisibleRangeChange = React.useCallback(
    (range: VisibleRange) => {
      if (!virtualMode) return
      const firstFlat = windowed.windowStart + range.firstIndex
      const lastFlat = windowed.windowStart + range.lastIndex
      windowed.ensureRange(firstFlat - VISIBLE_PAD, lastFlat + VISIBLE_PAD)

      if (windowed.total > 0) {
        const nextFraction = firstFlat / windowed.total
        setFraction((prev) =>
          Math.abs(prev - nextFraction) < FRACTION_EPSILON ? prev : nextFraction
        )
      }
    },
    [virtualMode, windowed.windowStart, windowed.total]
  )

  // month bucket fetch lands the window exactly at the month boundary — no partial first day
  const onJump = React.useCallback(
    (month: FlatMonth) => windowed.jumpToBucket({ key: month.key, itemsBefore: month.itemsBefore }),
    [windowed.jumpToBucket]
  )

  const onSentinelReached = () => {
    if (virtualMode) {
      const end = windowed.windowStart + windowed.items.length
      windowed.ensureRange(end, end + 1)
    } else if (fallbackMode && !pagination.allFetched && !pagination.isFetching) {
      pagination.fetchNext().catch(() => {})
    }
  }

  const onNearStart = () => {
    if (virtualMode && windowed.windowStart > 0) {
      windowed.ensureRange(windowed.windowStart - 1, windowed.windowStart)
    }
  }

  const reset = () => {
    windowed.reset()
    paginationReset()
  }

  const photos = fallbackMode ? pagination.data : windowed.items
  const scrubber: GalleryScrubberState | null =
    virtualMode && windowed.timeline && windowed.total > 0
      ? { timeline: windowed.timeline, fraction, onJump }
      : null

  return {
    photos,
    windowStart: fallbackMode ? 0 : windowed.windowStart,
    total: fallbackMode ? pagination.data.length : windowed.total,
    isFetching: fallbackMode ? pagination.isFetching : windowed.isFetching,
    virtualMode,
    // isFetching included so the sentinel observer re-arms after every fetch
    sentinelDisabled: fallbackMode
      ? pagination.allFetched || pagination.isFetching
      : windowed.allFetched || windowed.isFetching,
    onSentinelReached,
    onNearStart,
    onVisibleRangeChange,
    scrubber,
    galleryScrollTarget: windowed.scrollTarget
      ? { index: windowed.scrollTarget.flatIndex - windowed.windowStart, token: windowed.scrollTarget.token }
      : null,
    onScrollTargetApplied: windowed.clearScrollTarget,
    reset,
  }
}
